import { MinerChatMessage } from "@services/http/miners/dto";
import SocketUtils from "@services/http/socket/SocketUtils";
import ws, { SocketStatus } from "@services/http/socket/WmSocket";
import { useMessageListeners } from "@services/http/socket/hooks/MessageListeners";
import useReconnectListeners, { ReconnectListener } from "@services/http/socket/hooks/ReconnectListeners";
import { SocketActions, SocketSubTypes } from "@services/http/socket/requests";
import { RootDispatch, RootState } from "@store";
import AUTHSTATES from "@store/auth/AuthStates";
import { updateSocketStatus } from "@store/socket/actions";
import { dev } from "@utils/dev";
import { createContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AlgoSpeedEvent,
  ChartValueEvent,
  MinerBaseEvent,
  MinerHashEvent,
  MinerSettingsSpeedEvent,
  SocketEventTypes,
  SocketMessage,
  UserBaseEvent
} from "../../services/http/socket/responses";
import { logout } from "@store/auth/actions";

export const USER_CUSTOM_EVENT = "user-custom-event";

interface SubscribeMessageOptions {
  total?: boolean; // only for admin
  role?: string;
  only_admin?: boolean;
  only_users?: boolean;
}
interface SubscribeMessage extends SubscribeMessageOptions {
  action: SocketActions;
  type: SocketSubTypes;
  value: string | number | number[];
}

export type LiveValueListener = (message: SocketMessage<any>) => void;

type TId = number | string | undefined;

interface ISocketContext {
  subscribe: (
    type: SocketSubTypes,
    value: string | number | number[],
    options?: SubscribeMessageOptions
  ) => void;

  unsubscribe: (
    type: SocketSubTypes,
    value: string | number | number[],
    options?: SubscribeMessageOptions
  ) => void;

  addMessageListener: (
    type: SocketEventTypes,
    listener: LiveValueListener
  ) => void;

  removeMessageListener: (
    type: SocketEventTypes,
    listener: LiveValueListener
  ) => void;

  addReconnectListener: (
    listener: ReconnectListener
  ) => void;

  removeReconnectListener: (
    listener: ReconnectListener
  ) => void;
}

export const SocketContext = createContext<ISocketContext>({
  subscribe: () => { },
  unsubscribe: () => { },
  addMessageListener: (
    type: SocketEventTypes,
    listener: LiveValueListener
  ) => { },

  removeMessageListener: (
    type: SocketEventTypes,
    listener: LiveValueListener
  ) => { },

  addReconnectListener: () => { },
  removeReconnectListener: () => { },
});

export interface ICustomEventDetail {
  type: SocketEventTypes;
  event: MinerBaseEvent<string | undefined>;
}

export interface IUserEventDetail {
  type: SocketEventTypes;
  event: UserBaseEvent;
}

export interface ISubscription<T> {
  id: T;
  onlyUsers?: boolean;
  onlyAdmin?: boolean;
  isAlgo: boolean;
}

export const SocketState = ({ children }: { children: React.ReactNode }) => {

  const auth = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch() as RootDispatch;
  const listeners = useMessageListeners();
  const reconnectListeners = useReconnectListeners();

  useEffect(() => {
    if (auth.state === AUTHSTATES.AUTHORIZED) {
      ws.start({ onMessage, onReconnected, onStatusChanged });
      window.addEventListener('beforeunload', onBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', onBeforeUnload);
      ws.close();
    }
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      ws.close();
    };
  }, [auth.state]);

  function onBeforeUnload() {
    ws.close();
  }

  function onStatusChanged(status: SocketStatus) {
    dispatch(updateSocketStatus(status));
  }

  function onMessage(message: SocketMessage<any>): void {
    const event = SocketUtils.createEventForMiner(message);
    if (event) {
      document.dispatchEvent(event);
    }
    listeners.processMessage(message);
  }

  function onReconnected() {
    if (!ws) return;
    reconnectListeners.process();
  }

  function subscribe(
    type: SocketSubTypes,
    value: string | number | number[],
    options?: SubscribeMessageOptions
  ): void {
    if (!ws) return;
    const message: SubscribeMessage = {
      action: SocketActions.Subscribe,
      type,
      value,
      ...options,
    };
    ws.sendOrEnqueue(message);
  }

  function unsubscribe(
    type: SocketSubTypes,
    value: string | number | number[],
    options?: SubscribeMessageOptions
  ): void {
    if (!ws) return;
    const message: SubscribeMessage = {
      action: SocketActions.Unsubscribe,
      type,
      value,
      ...options,
    };
    ws.sendOrEnqueue(message);
  }

  // const subscribeMiner = (minerId: number) => {
  //   if (ws) {
  //     const message: SubscribeMessage = {
  //       action: SocketActions.Subscribe,
  //       type: SocketSubTypes.Miner,
  //       value: minerId,
  //     };
  //     ws.send(message);
  //   }
  // };

  // function unsubscribeMiner(minerId: number) {
  //   if (ws) {
  //     const message: SubscribeMessage = {
  //       action: SocketActions.Unsubscribe,
  //       type: SocketSubTypes.Miner,
  //       value: minerId,
  //     };
  //     ws.send(message);
  //   }
  // }

  // function subscribeHash(algo: string, total?: boolean) {
  //   if (ws) {
  //     const message = {
  //       action: SocketActions.Subscribe,
  //       type: SocketSubTypes.Hash,
  //       value: algo,
  //       total,
  //     };
  //     ws.send(message);
  //   }
  // }

  // function unsubscribeHash(algo: string, total?: boolean) {
  //   if (ws) {
  //     ws.send({
  //       action: SocketActions.Unsubscribe,
  //       type: SocketSubTypes.Hash,
  //       value: algo,
  //       total,
  //     });
  //   }
  // }

  // function subscribePool(poolId: string) {
  //   if (ws) {
  //     dev.log("subscribePool", { poolId });
  //     const message = {
  //       action: SocketActions.Subscribe,
  //       type: SocketSubTypes.Setting,
  //       value: poolId,
  //     };
  //     ws.send(message);
  //   }
  // }

  // function unsubscribePool(poolId: string) {
  //   if (ws) {
  //     dev.log("UNsubscribePool", { poolId });
  //     ws.send({
  //       action: SocketActions.Unsubscribe,
  //       type: SocketSubTypes.Setting,
  //       value: poolId,
  //     });
  //   }
  // }

  // function subscribeLocation(id: string) {
  //   if (!ws) return;
  //   const message = {
  //     action: SocketActions.Subscribe,
  //     type: SocketSubTypes.Location,
  //     value: id
  //   };
  //   ws.send(message);
  // }

  // function unsubscribeLocation(id: string) {
  //   if (!ws) return;
  //   ws.send({
  //     action: SocketActions.Unsubscribe,
  //     type: SocketSubTypes.Location,
  //     value: id
  //   });
  // }

  // function subscribeObject(id: string) {
  //   if (!ws) return;
  //   ws.send({
  //     action: SocketActions.Subscribe,
  //     type: SocketSubTypes.Object,
  //     value: id
  //   });
  // }

  // function unsubscribeObject(id: string) {
  //   if (!ws) return;
  //   ws.send({
  //     action: SocketActions.Unsubscribe,
  //     type: SocketSubTypes.Object,
  //     value: id
  //   });
  // }

  // function subscribeMiners(ids: number[]) {
  //   if (!ws) return;
  //   ws.send({
  //     action: SocketActions.Subscribe,
  //     type: SocketSubTypes.Miner,
  //     value: ids
  //   });
  // }

  // function unsubscribeMiners(ids: number[]) {
  //   if (!ws) return;
  //   ws.send({
  //     action: SocketActions.Unsubscribe,
  //     type: SocketSubTypes.Miner,
  //     value: ids
  //   });
  // }

  // function subscribeClient(id: number, role: string) {
  //   if (!ws) return;
  //   ws.send({
  //     action: SocketActions.Subscribe,
  //     type: SocketSubTypes.Client,
  //     value: id,
  //     role
  //   });
  // }

  // function unsubscribeClient(id: number, role: string) {
  //   if (!ws) return;
  //   ws.send({
  //     action: SocketActions.Unsubscribe,
  //     type: SocketSubTypes.Client,
  //     value: id,
  //     role,
  //   });
  // }

  return (
    <SocketContext.Provider
      value={{
        subscribe,
        unsubscribe,
        // subscribeMiner,
        // unsubscribeMiner,

        // subscribeMiners,
        // unsubscribeMiners,

        // subscribeHash,
        // unsubscribeHash,

        // subscribePool,
        // unsubscribePool,

        // subscribeLocation,
        // unsubscribeLocation,

        // subscribeObject,
        // unsubscribeObject,

        // subscribeClient,
        // unsubscribeClient,

        addMessageListener: listeners.addMessageListener,
        removeMessageListener: listeners.removeMessageListener,
        addReconnectListener: reconnectListeners.add,
        removeReconnectListener: reconnectListeners.remove,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
